import { useState } from "react"
import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import LicenseCollectionOwnerDialog from "@/v2-backoffice-client/license/collections/LicenseCollectionOwnerDialog"
import LicenseInvoiceFollowUpDialog from "@/v2-backoffice-client/license/collections/LicenseInvoiceFollowUpDialog"
import { useInvoiceFollowUpDeleteMutation } from "@/v2-backoffice-client/license/collections/license.invoiceFollowUp.delete.mutation"
import {
  buildFinanceHandoffMailto,
  buildPaymentRequestMailto,
} from "@/v2-backoffice-client/license/collections/license.collections.utils"
import { evictInvoiceCollectionQueries } from "@/v2-backoffice-client/license/license.utils"
import { FENERUM_INVOICE_BASE_URL } from "@/v2-backoffice-client/constants"
import type { InvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/__types__/license.collections.query"

export type LicenseCollectionRowToolbarProps = {
  invoice: InvoiceCollectionsQuery["getInvoiceCollections"][number]
  /** The customer's still-open rows — the payment-request draft names them all. */
  customerOpenInvoices: InvoiceCollectionsQuery["getInvoiceCollections"][number][]
}

// A collections row's actions: update the conversation (the row editor),
// jump to the customer or the invoice in Fenerum (where the PDF for the
// payment-reminder email lives), report a dispute to finance with the
// customer name pre-filled as the subject — and, only for rows started by
// mistake, remove the record. There is deliberately no "mark paid": Fenerum
// registers payments every morning and the mirror resolves the row.
function LicenseCollectionRowToolbar(props: LicenseCollectionRowToolbarProps) {
  const { invoice, customerOpenInvoices } = props
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isOwnerOpen, setIsOwnerOpen] = useState(false)

  // The drafts cover the row's own invoice even when the account is
  // unlinked (no CustomerId → customerOpenInvoices is empty).
  const paymentRequestInvoices =
    customerOpenInvoices.length > 0 ? customerOpenInvoices : [invoice]

  const [deleteFollowUp, { loading, error }] = useInvoiceFollowUpDeleteMutation(
    {
      update: evictInvoiceCollectionQueries,
      onCompleted() {
        toast({
          variant: "success",
          title: "Follow-up removed",
          description: `Invoice ${invoice.InvoiceNumber ?? invoice.InvoiceId} is back to "Not started".`,
        })
      },
    },
  )

  return (
    <>
      <ActionMenu>
        <ActionMenu.Trigger />
        <ActionMenu.Content>
          <ActionMenu.Item
            iconSrc="edit"
            data-feature-code="UI_BackofficeCollections_Row_Update"
            onSelect={(e) => {
              e.preventDefault()
              setIsEditOpen(true)
            }}
          >
            Update Follow-up
          </ActionMenu.Item>

          {invoice.CustomerId != null && (
            <ActionMenu.Item
              iconSrc="user-pen"
              data-feature-code="UI_BackofficeCollections_Row_AssignOwner"
              onSelect={(e) => {
                e.preventDefault()
                setIsOwnerOpen(true)
              }}
            >
              Update Owner
            </ActionMenu.Item>
          )}

          {invoice.ExternalCustomerId && (
            <ActionMenu.LinkItem
              iconSrc="expand"
              to={`>>?v=customer:${invoice.ExternalCustomerId}`}
            >
              Open Customer
            </ActionMenu.LinkItem>
          )}

          <ActionMenu.LinkItem
            iconSrc="bank"
            to={`${FENERUM_INVOICE_BASE_URL}/${invoice.ExternalInvoiceUuid}/`}
            external
          >
            Open in Fenerum
          </ActionMenu.LinkItem>

          {/* Bokmål draft naming everything the customer owes, opened in
              the owner's own mail client — read, edit, send (C5: never
              auto-sent). */}
          {invoice.OutcomeCode == null && (
            <ActionMenu.LinkItem
              iconSrc="envelope"
              to={buildPaymentRequestMailto(paymentRequestInvoices)}
              external
            >
              Draft Payment Request
            </ActionMenu.LinkItem>
          )}

          {/* Today's convention (arbo@, customer name as subject) with the
              full context in the body — org number, invoice, Fenerum link,
              the dispute note. */}
          <ActionMenu.LinkItem
            iconSrc="envelope"
            to={buildFinanceHandoffMailto(invoice, invoice.Note)}
            external
          >
            Email Finance
          </ActionMenu.LinkItem>

          {invoice.FollowUpId != null && (
            <ActionMenu.ConfirmItem
              iconSrc="trash"
              variant="destructive"
              isLoading={loading}
              error={error}
              errorTitle="Failed to remove follow-up"
              confirmTitle="Remove Follow-up"
              confirmDescription={`Removes the follow-up record on invoice ${invoice.InvoiceNumber ?? invoice.InvoiceId} — status, promised date and note. Use it for rows started by mistake; the invoice stays on the board while it is outstanding.`}
              confirmText="Remove"
              confirmVariant="destructive"
              data-feature-code="UI_BackofficeCollections_Row_Delete"
              onConfirm={async () => {
                if (invoice.FollowUpId == null) return
                await deleteFollowUp({ variables: { id: invoice.FollowUpId } })
              }}
            >
              Remove Record
            </ActionMenu.ConfirmItem>
          )}
        </ActionMenu.Content>
      </ActionMenu>

      {/* Mounted per open so the fields re-seed from the row every time. */}
      {isEditOpen && (
        <LicenseInvoiceFollowUpDialog
          invoice={invoice}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}

      {isOwnerOpen && (
        <LicenseCollectionOwnerDialog
          invoice={invoice}
          open={isOwnerOpen}
          onOpenChange={setIsOwnerOpen}
        />
      )}
    </>
  )
}

export default LicenseCollectionRowToolbar
